/**
 * src/components/shared/WizardShell.jsx
 *
 * The wizard archetype (docs/ui-component-map-and-governance.md §3.5): an `lg`
 * Modal with a numbered step rail (tick on done steps, current step in
 * accent, a failed step marked), one step body, and the footer
 * [Back] … [Next | Finish]. Cancel is the header ✕, never a footer button.
 *
 * Step rules, because they encode the order a management-system step is
 * worked through:
 *   - Next and Finish run the step's `validate()` first. A returned message
 *     blocks the move, is shown above the footer, and marks the step on the
 *     rail. `validate` may be async (a step that commits its own write).
 *   - Back only moves the index. Step state belongs to the caller, so going
 *     back never discards what was entered.
 *   - ✕ runs the Modal's unsaved-changes guard (`dirty`).
 *   - Below 600 px the rail collapses to "Step N of M".
 */
import { useState } from 'react';
import Modal from './Modal.jsx';
import Button from './Button.jsx';

/**
 * @typedef {Object} WizardStep
 * @property {string} id
 * @property {string} label
 * @property {import('react').ReactNode | (() => import('react').ReactNode)} body
 * @property {() => (string | null | undefined | void | Promise<string | null | undefined | void>)} [validate]
 *   Returns an error message to block, or nothing to proceed.
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.title
 * @param {WizardStep[]} props.steps
 * @param {() => void} props.onClose
 * @param {() => void} props.onFinish  Called after the last step validates.
 * @param {boolean} [props.dirty]
 * @param {string} [props.dirtyMessage]  Confirm text for ✕ while dirty (see Modal).
 * @param {boolean} [props.closeOnBackdrop]  See Modal; false for a permanent close.
 * @param {number} [props.initialStep]
 * @param {(index: number) => void} [props.onStepChange]
 */
export default function WizardShell({
  title,
  steps,
  onClose,
  onFinish,
  dirty,
  dirtyMessage,
  closeOnBackdrop,
  initialStep = 0,
  onStepChange,
}) {
  const [index, setIndex] = useState(Math.min(Math.max(initialStep, 0), steps.length - 1));
  const [failed, setFailed] = useState(/** @type {Record<string, string>} */ ({}));
  const [busy, setBusy] = useState(false);

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const error = failed[step.id];

  const advance = async () => {
    if (busy) return;
    setBusy(true);
    let message;
    try {
      message = step.validate ? await step.validate() : null;
    } catch {
      // A step that throws instead of returning a message never shows its raw
      // error text to the user; the step owns any specific wording it needs.
      message = 'This step could not be completed. Try again.';
    }
    setBusy(false);
    if (message) {
      setFailed((f) => ({ ...f, [step.id]: String(message) }));
      return;
    }
    setFailed((f) => {
      const { [step.id]: _cleared, ...rest } = f;
      return rest;
    });
    if (isLast) onFinish();
    else {
      setIndex(index + 1);
      onStepChange?.(index + 1);
    }
  };

  const back = () => {
    if (index > 0 && !busy) {
      setIndex(index - 1);
      onStepChange?.(index - 1);
    }
  };

  const footer = (
    <div className="action-footer wizard-footer" data-testid="wizard-footer">
      <div className="action-footer-destructive">
        {index > 0 && (
          <Button variant="ghost" onClick={back} disabled={busy} data-action="back">
            Back
          </Button>
        )}
      </div>
      <div className="action-footer-main">
        <Button variant="primary" onClick={advance} disabled={busy} data-action="primary">
          {isLast ? 'Finish' : 'Next'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={title}
      onClose={onClose}
      size="lg"
      dirty={dirty}
      dirtyMessage={dirtyMessage}
      closeOnBackdrop={closeOnBackdrop}
      footer={footer}
    >
      <ol className="wizard-rail" aria-label="Steps" data-testid="wizard-rail">
        {steps.map((s, i) => {
          const state = failed[s.id]
            ? 'failed'
            : i < index
              ? 'done'
              : i === index
                ? 'current'
                : 'todo';
          return (
            <li
              key={s.id}
              className="wizard-rail-step"
              data-state={state}
              aria-current={i === index ? 'step' : undefined}
            >
              <span className="wizard-rail-marker">{state === 'done' ? '✓' : i + 1}</span>
              <span className="wizard-rail-label">{s.label}</span>
            </li>
          );
        })}
      </ol>
      <div className="wizard-rail-compact" data-testid="wizard-rail-compact">
        Step {index + 1} of {steps.length}
        <span style={{ color: 'var(--fg2)' }}> · {step.label}</span>
      </div>
      <div className="wizard-body" data-step={step.id}>
        {typeof step.body === 'function' ? step.body() : step.body}
      </div>
      {error && (
        <div role="alert" className="wizard-error">
          {error}
        </div>
      )}
    </Modal>
  );
}
